import React, { useState, useEffect, useContext } from 'react';
import "./moderatePost.css";
import { MoreVert, Delete } from "@mui/icons-material";
import S3Image from '../S3Image/S3Image';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { deleteCommunityPost, updateCommunityPost } from '../../graphql/mutations';
import { UserContext } from '../../App';
import {
  Menu, MenuItem, IconButton, Snackbar, Dialog, DialogContent, DialogTitle, Button, DialogActions,
  DialogContentText, Chip, Box, Typography, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';

import { useNavigate } from 'react-router-dom';

export default function ModeratePost({ post: initialPost, removePost }) {
  const [post, setPost] = useState(initialPost);
  const [anchorEl, setAnchorEl] = useState(null);
  const userData = useContext(UserContext);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [guidanceContent, setGuidanceContent] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (post?.mediaS3Key && post?.mediaS3Key.endsWith('.mp4')) {
      (async () => {
        const url = await Storage.get(post.mediaS3Key);
        setMediaUrl(url);
      })();
    }
  }, [post]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleApproveClick = () => {
    setActionType('approve');
    setConfirmOpen(true);
  };

  const handleDeleteClick = () => {
    setActionType('delete');
    setConfirmOpen(true);
  };

  const handleApprovePostConfirm = async () => {
    await handleApprovePost();
    removePost(post.id);
    setConfirmOpen(false);
    setShowSnackbar(true);
  };

  const handleDeletePostConfirm = async () => {
    await handleDeletePost();
    setConfirmOpen(false);
    setShowSnackbar(true);
  };

  const handleDeletePost = async () => {
    try {
      await API.graphql(graphqlOperation(deleteCommunityPost, { input: { id: post.id } }));
      removePost(post.id);
      setShowSnackbar(true);
    } catch (error) {
      console.error('Error deleting post', error);
    }
    handleMenuClose();
  };

  const handleApprovePost = async () => {
    try {
      await API.graphql(graphqlOperation(updateCommunityPost, {
        input: {
          id: post.id,
          needsModeration: false
        }
      }));
      setPost(prevPost => ({ ...prevPost, needsModeration: false }));
    } catch (error) {
      console.error("Error approving post:", error);
    }
  };

  return (
    <div className="post">
      <div className="postWrapper">
        {/* Post Header */}
        <div className="postTop">
          <div className="postTopLeft">
            {post?.User?.image ? (
              <S3Image S3Key={post?.User?.image} S3height={128} className="postProfileImg" />
            ) : (
              <img className="postProfileImg" src={post?.User?.image || 'assets/persons/default.png'} alt="User" />
            )}
            <div>
              <Typography variant="h6" className="postUsername">
                {post?.User?.firstName ? `${post.User.firstName} ${post.User.lastName}` : post.User.name}
              </Typography>
              <Typography variant="body2" className="postDate">
                {new Date(post.dateTime).toLocaleString()}
              </Typography>
            </div>
          </div>

          {/* Moderation Score & Label */}
          <Box className="moderationInfo">
            <Chip label={`Score: ${post.moderationScore.toFixed(3)}`} color="warning" variant="outlined" />
            <Tooltip title="Moderation Label">
              <Chip label={post.moderationLabel || "No Label"} color="secondary" />
            </Tooltip>
          </Box>

          {/* Action Buttons */}
          <div className="moderationButtons">
            <Button startIcon={<CheckCircleOutline />} onClick={handleApproveClick} variant="contained" color="success">
              Approve
            </Button>
            <Button startIcon={<Delete />} onClick={handleDeleteClick} variant="contained" color="error">
              Delete
            </Button>
          </div>
        </div>

        {/* Post Content */}
        <div className="postCenter">
          <Typography variant="body1" className="postText">{post.text}</Typography>

          {/* Media Handling */}
          {mediaUrl && post?.mediaS3Key?.endsWith('.mp4') ? (
            <video controls className="postVideo">
              <source src={mediaUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : post?.mediaS3Key ? (
            <S3Image S3Key={post.mediaS3Key} S3height={350} className="postImg" />
          ) : null}
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message={actionType === 'delete' ? "Post deleted successfully" : "Post approved successfully"}
        action={
          <IconButton size="small" color="inherit" onClick={() => setShowSnackbar(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{"Are you sure?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionType === 'delete' ? 'Do you really want to delete this post?' : 'Do you want to approve this post?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">Cancel</Button>
          <Button onClick={actionType === 'delete' ? handleDeletePostConfirm : handleApprovePostConfirm} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
